// Lib
import React, { useCallback, useMemo, useState } from 'react';
import { Typography, Box } from '@mui/material';
import { format } from 'date-fns';

// Components
import OrderInfoItem from './OrderInfoItem';
import ToggleButton from '../../../Button/ToggleButton';
import Feather from '../../../Feather';

// Hooks
import useUI from '../../../../hooks/useUI';

// Utils
import { pickIf } from '../../../../utils/pickIf';

import { useStyles } from './OrderInfoStyles';

const OrderInfo = ({
  order,
  addressesCount,
  isApproved = false,
  isMethodPage = false,
  onOpenShippingModal,
  className,
}) => {
  const { isDesktopMode } = useUI();
  const [open, setOpen] = useState(false);
  const { classes, cx } = useStyles({ open });

  const isDistro = useMemo(() => addressesCount > 1, [addressesCount]);

  const {
    order_sn: orderId,
    created: orderDate,
    user: originator,
    addresses,
    shipment_method: method,
    additional_info_limit: comment,
    additional_info_shipment: shippingComment,
  } = order || {};

  const onToggle = useCallback(
    () => setOpen((prevState) => !prevState),
    [setOpen],
  );

  const date = useMemo(() => {
    if (orderDate) {
      return format(new Date(orderDate), 'MM/dd/yyyy');
    }
  }, [orderDate]);

  return (
    <>
      {isDesktopMode ? (
        <>
          <Typography mb={3} variant="h2">
            Order Info
          </Typography>
          <div className={cx(className, classes.root)}>
            <OrderInfoItem
              width={pickIf(isDistro, '10%', '8%')}
              title="Order ID"
              text={orderId}
            />
            <OrderInfoItem width="8%" title="Order date" text={date} />
            <OrderInfoItem
              width={pickIf(isDistro, '17%', '15%')}
              textColor="#C5203F"
              title="Originator"
              text={originator}
            />
            <OrderInfoItem
              width={pickIf(isDistro, '15%', '25%')}
              title={pickIf(isDistro, 'Total orders', 'Ship-to address')}
              text={pickIf(isDistro, addressesCount, addresses?.full_address)}
            />
            <OrderInfoItem
              width={isDistro ? '20%' : '16%'}
              title="Shipping method"
              text={method}
              isMethodPage={isMethodPage}
              isApproved={isApproved}
              onOpenShippingModal={onOpenShippingModal}
            />
            <OrderInfoItem
              width="24%"
              title="Comment"
              text={pickIf(isMethodPage, shippingComment, comment)}
            />
          </div>
        </>
      ) : (
        <>
          <Box display="flex">
            <Typography className={classes.orderInfoTitle} variant="h3">
              Order Info
            </Typography>
            <ToggleButton
              open={open}
              onToggle={onToggle}
              className={classes.toggleButton}
              DownElement={<Feather printType="stroke" type="arrowDownSmall" />}
              UpElement={<Feather printType="stroke" type="arrowUpSmall" />}
            />
          </Box>
          {open && (
            <>
              <OrderInfoItem
                className={cx(classes.orderInfo, classes.orderInfoId)}
                title="Order ID"
                text={orderId}
              />
              <OrderInfoItem
                className={classes.orderInfo}
                title="Order date"
                text={date}
              />
              <OrderInfoItem
                className={classes.orderInfo}
                textColor="#C5203F"
                title="Originator"
                text={originator}
              />
              {!isDistro && (
                <OrderInfoItem
                  className={classes.orderInfo}
                  title="Ship-to address"
                  text={addresses.full_address}
                />
              )}
              {!isMethodPage && (
                <OrderInfoItem
                  className={classes.orderInfo}
                  title="Shipping method"
                  text={method}
                />
              )}
              <OrderInfoItem
                className={cx(classes.orderInfo, classes.lastOrderInfo)}
                title="Comment"
                text={comment}
              />
            </>
          )}
        </>
      )}
    </>
  );
};

export default OrderInfo;
